import { t } from '@luna/i18n';
import type { Country } from '@shared/info-api';
import { getSafeCountryName } from '@shared/info-api';
import { isFeatureEnabled } from '@luna/gatekeeper';
import { isDefined } from '@luna/utils';
import { getFeatureConfig } from '@luna/feature-config';

import {
    getContactByIdFromContacts,
    getPrimaryContact,
    isContactEntryAllowed,
} from './ContactService';
import type { Address } from './ContactTypes';
import { getAddresses } from './AddressesSourceService';
import { ContactListFlag } from './ContactDataManagementTypes';
import { STREET_TYPE_ITEMS } from './StreetTypeSelectService';

const ADDRESS_DELIMITER = ', ';

export function getAddressById(id?: string): Address | undefined {
    return getContactByIdFromContacts(id, getAddresses());
}

export function getPrimaryAddress(): Address | undefined {
    return getPrimaryContact(getAddresses());
}

export function getStreetTypeLabel(streetTypeValue?: string): string | undefined {
    const streetType = STREET_TYPE_ITEMS.find(({ value }) => value === streetTypeValue);
    return streetType?.label;
}

function getStreet(address: Omit<Address, 'id'>): string {
    const { streetTypeSelect, streetTypeField } = getFeatureConfig(
        ({ contactDataManagement }) => contactDataManagement.address,
    );
    const streetParts = [
        address.street,
        streetTypeSelect ? getStreetTypeLabel(address.streetType) : undefined,
        streetTypeField ? address.streetType : undefined,
        address.streetNumber,
    ];

    return streetParts.filter(isDefined).join(' ');
}

function getSpecificBuildingInfo({
    buildingName,
    stair,
    floor,
    apartment,
}: Omit<Address, 'id'>): string | undefined {
    const parts = [
        buildingName && t('contactDataManagement.formattedAddressBuildingName', { buildingName }),
        stair && t('contactDataManagement.formattedAddressStair', { stair }),
        floor && t('contactDataManagement.formattedAddressFloor', { floor }),
        apartment && t('contactDataManagement.formattedAddressApartment', { apartment }),
    ].filter(isDefined);

    if (parts.length === 0) {
        return undefined;
    }

    return parts.join(ADDRESS_DELIMITER);
}

function getPoBoxOrStreet(address: Omit<Address, 'id'>): string {
    return address.postBox ?? getStreet(address);
}

export function getAddressParts(
    address?: Omit<Address, 'id'>,
    countries: Country[] = [],
): string[] {
    if (!address) {
        return [];
    }

    const countryName = getSafeCountryName(countries, address.country);
    const addressParts = [
        getPoBoxOrStreet(address),
        getSpecificBuildingInfo(address),
        address.additionalData,
        `${address.zipCode} ${address.city}`,
        address.county,
        countryName,
    ];

    return addressParts.filter(isDefined);
}

export function formatAddress(address?: Omit<Address, 'id'>, countries: Country[] = []): string {
    return getAddressParts(address, countries).filter(isDefined).join(ADDRESS_DELIMITER);
}

export function isAddressEntryAllowed(flags: ContactListFlag[] | undefined): boolean {
    if (!isFeatureEnabled((f) => f.contactDataManagement) || !flags) {
        return false;
    }
    return isContactEntryAllowed(flags, ContactListFlag.ADDRESS_NOT_ALLOWED);
}
